import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/Card'
import { Badge } from 'components/ui/Badge'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Modal } from 'components/ui/Modal'
import { teamsApi, candidatesApi, type Team, type Candidate } from 'lib/api'
import { useToast } from 'contexts/ToastContext'
import { useAuth } from 'contexts/AuthContext'
import { AddCandidatesModal } from 'features/candidates/components/AddCandidatesModal'
import { PageHeader } from 'components/layout/PageHeader'
import { 
  ArrowLeft,
  Plus, 
  Search, 
  Users, 
  UserPlus, 
  UserMinus,
  Edit3,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  User,
  Upload,
  Archive,
  ArchiveRestore,
  CheckCircle
} from 'lucide-react'

export function TeamMembers() {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const { teamId } = useParams<{ teamId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { hasPermission } = useAuth()
  const currentPath = `${location.pathname}${location.search}${location.hash}`
  
  const [team, setTeam] = useState<Team | null>(null)
  const [allCandidates, setAllCandidates] = useState<Candidate[]>([])
  const [teamMembers, setTeamMembers] = useState<Candidate[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddMemberModal, setShowAddMemberModal] = useState(false)
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false)
  const [memberToRemove, setMemberToRemove] = useState<Candidate | null>(null)
  const [updating, setUpdating] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const canMassImport = hasPermission('upload_cvs')

  useEffect(() => {
    if (teamId) {
      fetchTeamData()
    }
  }, [teamId])

  const fetchTeamData = async () => {
    try {
      setLoading(true)
      const [teamResponse, candidatesList] = await Promise.all([
        teamsApi.getById(parseInt(teamId!)),
        candidatesApi.getAllPages()
      ])
      
      setTeam(teamResponse.data)
      setAllCandidates(candidatesList || [])
      setTeamMembers(teamResponse.data.candidates || [])
    } catch (error) {
      console.error('Error fetching team data:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('teamMembers.messages.fetchFailed'),
        duration: 5000
      })
    } finally {
      setLoading(false)
    }
  }

  const handleImportComplete = async () => {
    await fetchTeamData()
    setShowImportModal(false)
  }

  const handleAddMembers = async (candidates: Candidate[]) => {
    if (!team || candidates.length === 0) return

    try {
      setUpdating(true)
      const currentMemberIds = teamMembers.map(member => member.id)
      const newMemberIds = candidates.map(candidate => candidate.id)
      const updatedMemberIds = [...currentMemberIds, ...newMemberIds]
      
      await teamsApi.update(team.id, { candidate_ids: updatedMemberIds })
      
      setTeamMembers(prev => [...prev, ...candidates])
      setShowAddMemberModal(false)
      
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('teamMembers.messages.membersAdded', { count: candidates.length }),
        duration: 3000
      })
    } catch (error) {
      console.error('Error adding members:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('teamMembers.messages.addFailed'),
        duration: 5000
      })
    } finally {
      setUpdating(false)
    }
  }

  const handleRemoveMember = async () => {
    if (!team || !memberToRemove) return

    try {
      setUpdating(true)
      const updatedMemberIds = teamMembers
        .filter(member => member.id !== memberToRemove.id)
        .map(member => member.id)
      
      await teamsApi.update(team.id, { candidate_ids: updatedMemberIds })
      
      setTeamMembers(prev => prev.filter(member => member.id !== memberToRemove.id))
      setShowRemoveConfirm(false)
      setMemberToRemove(null)
      
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('teamMembers.messages.memberRemoved'),
        duration: 3000
      })
    } catch (error) {
      console.error('Error removing member:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('teamMembers.messages.removeFailed'),
        duration: 5000
      })
    } finally {
      setUpdating(false)
    }
  }

  const toggleArchiveStatus = async () => {
    if (!team) return

    try {
      setArchiving(true)
      const response = await teamsApi.update(team.id, { is_archived: !team.is_archived })
      setTeam(response.data)
      addToast({
        type: 'success',
        title: t('common.success'),
        message: team.is_archived
          ? t('teams.messages.teamUnarchived')
          : t('teams.messages.teamArchived'),
        duration: 3000
      })
    } catch (error) {
      console.error('Error toggling team archive state from members page:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('teams.messages.teamArchiveFailed'),
        duration: 5000
      })
    } finally {
      setArchiving(false)
    }
  }

  const openRemoveConfirm = (member: Candidate) => {
    setMemberToRemove(member)
    setShowRemoveConfirm(true)
  }

  const availableCandidates = allCandidates.filter(candidate => 
    !teamMembers.some(member => member.id === candidate.id) &&
    (candidate.name && candidate.name.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!team) {
    return (
      <div className="text-center py-12 max-[1149px]:py-8 max-[1149px]:px-4">
        <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4 max-[1149px]:h-8 max-[1149px]:w-8 max-[1149px]:mb-3" />
        <p className="text-muted-foreground text-lg max-[1149px]:text-base">{t('teamMembers.teamNotFound')}</p>
        <Button onClick={() => navigate('/teams')} className="mt-4 max-[1149px]:mt-3 max-[1149px]:h-9 max-[1149px]:text-sm">
          <ArrowLeft className="h-4 w-4 mr-2 max-[1149px]:mr-0" />
          <span className="max-[1149px]:text-sm">{t('teamMembers.backToTeams')}</span>
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-[1149px]:px-4 max-[1149px]:py-4 max-[1149px]:space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between max-[1149px]:flex-col max-[1149px]:items-stretch max-[1149px]:gap-4">
        <div className="flex items-center space-x-4 max-[1149px]:space-x-3 max-[1149px]:w-full">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/teams')}
            className="flex items-center space-x-2 max-[1149px]:h-9"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="max-[1149px]:hidden">{t('common.back')}</span>
          </Button>
          <PageHeader
            className="flex-1 min-w-0"
            title={
              <span className="flex items-center gap-3 max-[1149px]:gap-2">
                <span
                  className="h-4 w-4 shrink-0 rounded-full max-[1149px]:h-3 max-[1149px]:w-3"
                  style={{ backgroundColor: team.color }}
                />
                {team.name}
              </span>
            }
            titleClassName="max-[1149px]:truncate"
            description={t('teamMembers.subtitle', { count: teamMembers.length })}
          />
        </div>
        <div className="flex items-center gap-2 max-[1149px]:w-full max-[1149px]:flex-col">
          <Button onClick={() => setShowAddMemberModal(true)} className="max-[1149px]:w-full max-[1149px]:h-9 max-[1149px]:text-sm">
            <UserPlus className="h-4 w-4 mr-2 max-[1149px]:mr-0" />
            <span className="max-[1149px]:text-sm">{t('teamMembers.addMember')}</span>
          </Button>
        </div>
      </div>

      {/* Team Info Card */}
      <Card>
        <CardHeader className="max-[1149px]:p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CardTitle className="flex items-center space-x-2 max-[1149px]:text-base">
                <Users className="h-5 w-5 max-[1149px]:h-4 max-[1149px]:w-4" />
                <span>{t('teamMembers.teamInfo')}</span>
              </CardTitle>
              <Badge
                variant="secondary"
                className={`text-xs px-2.5 py-0.5 rounded-full border-0 ${
                  team.is_archived
                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                    : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
                }`}
              >
                {team.is_archived
                  ? t('teams.archivedBadge')
                  : t('teams.activeBadge', 'Actif')}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleArchiveStatus}
                disabled={archiving}
                className="h-8 px-3 text-xs max-[1149px]:h-8"
                title={team.is_archived ? t('teams.unarchive') : t('teams.archive')}
              >
                {archiving ? (
                  <div className="h-3.5 w-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                ) : team.is_archived ? (
                  <>
                    <ArchiveRestore className="h-3.5 w-3.5 mr-1.5" />
                    <span className="hidden sm:inline">{t('teams.unarchive')}</span>
                  </>
                ) : (
                  <>
                    <Archive className="h-3.5 w-3.5 mr-1.5" />
                    <span className="hidden sm:inline">{t('teams.archive')}</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="max-[1149px]:p-4 space-y-4">
          <div className="grid gap-4 md:grid-cols-3 max-[1149px]:grid-cols-1 max-[1149px]:gap-3">
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">{t('teamMembers.totalMembers')}</div>
              <div className="text-2xl font-bold max-[1149px]:text-xl">{teamMembers.length}</div>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">{t('teamMembers.createdAt')}</div>
              <div className="text-sm max-[1149px]:text-xs">{new Date(team.created_at).toLocaleDateString()}</div>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">{t('teamMembers.description')}</div>
              <div className="text-sm max-[1149px]:text-xs">{team.description || t('teamMembers.noDescription')}</div>
            </div>
          </div>

          {/* Associated offers summary */}
          <div className="border-t pt-3 mt-1 max-[1149px]:pt-2 max-[1149px]:mt-0">
            <div className="flex items-center justify-between max-[1149px]:flex-col max-[1149px]:items-start max-[1149px]:gap-2">
              <div>
                <div className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">
                  {t('teams.associatedOffersCount', { count: team.offers?.length || 0 })}
                </div>
                {(!team.offers || team.offers.length === 0) && (
                  <p className="text-xs text-muted-foreground max-[1149px]:text-[11px]">
                    {t('teams.noOffers')}
                  </p>
                )}
              </div>
              {team.offers && team.offers.length > 0 && (
                <div className="flex flex-wrap gap-1 max-[1149px]:gap-0.5 max-w-full">
                  {team.offers.slice(0, 3).map((offer) => (
                    <button
                      key={offer.id}
                      type="button"
                      onClick={() => navigate(`/offers/${offer.id}`, { state: { from: currentPath } })}
                      className="inline-flex items-center max-w-[180px] rounded-full border border-border bg-muted/60 px-2.5 py-0.5 text-[11px] text-foreground hover:bg-muted hover:text-foreground transition-colors"
                      title={offer.job_title}
                    >
                      <span className="truncate max-w-[160px]">
                        {offer.job_title}
                      </span>
                    </button>
                  ))}
                  {team.offers.length > 3 && (
                    <span className="text-xs text-muted-foreground max-[1149px]:text-[11px]">
                      +{team.offers.length - 3} {t('teams.card.more')}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="flex items-center justify-between bg-background p-4 rounded-lg border max-[1149px]:flex-col max-[1149px]:items-stretch max-[1149px]:gap-3 max-[1149px]:p-3">
        <div className="flex items-center space-x-4 max-[1149px]:w-full">
          <div className="relative max-[1149px]:flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
            <Input
              placeholder={t('teamMembers.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64 max-[1149px]:w-full max-[1149px]:h-9"
            />
          </div>
        </div>
        
        <div className="flex items-center space-x-2 text-sm text-muted-foreground max-[1149px]:text-xs max-[1149px]:justify-center">
          <span>{t('teamMembers.showingMembers', { count: teamMembers.length })}</span>
        </div>
      </div>

      {/* Members List */}
      {teamMembers.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center max-[1149px]:p-8">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4 max-[1149px]:h-8 max-[1149px]:w-8 max-[1149px]:mb-3" />
            <p className="text-muted-foreground text-lg max-[1149px]:text-base">{t('teamMembers.noMembers')}</p>
            <p className="text-sm text-muted-foreground mt-1 max-[1149px]:text-xs">
              {t('teamMembers.noMembersDesc')}
            </p>
            <Button 
              onClick={() => setShowAddMemberModal(true)}
              className="mt-4 max-[1149px]:mt-3 max-[1149px]:h-9 max-[1149px]:text-sm"
            >
              <UserPlus className="h-4 w-4 mr-2 max-[1149px]:mr-0" />
              <span className="max-[1149px]:text-sm">{t('teamMembers.addFirstMember')}</span>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 max-[1149px]:grid-cols-1 max-[1149px]:gap-3">
          {teamMembers.map((member) => (
            <Card 
              key={member.id} 
              className="hover:shadow-lg transition-all duration-200 cursor-pointer hover:border-primary/50 hover:scale-[1.02] group max-[1149px]:hover:scale-100"
              onClick={() => navigate(`/candidates/${member.id}`, { state: { from: currentPath } })}
              title={t('teamMembers.clickToView')}
            >
              <CardHeader className="pb-3 max-[1149px]:p-4 max-[1149px]:pb-2">
                <div className="flex items-center justify-between max-[1149px]:gap-2">
                  <div className="flex items-center space-x-3 max-[1149px]:space-x-2 flex-1 min-w-0">
                    <img
                      src={member.profile_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name || member.email || 'User')}&background=random&color=fff&size=128`}
                      alt={member.name}
                      className="w-10 h-10 rounded-full object-cover border-2 border-border flex-shrink-0 max-[1149px]:w-8 max-[1149px]:h-8"
                      onError={(e) => {
                        // Fallback if image fails to load
                        const target = e.target as HTMLImageElement
                        target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name || member.email || 'User')}&background=random&color=fff&size=128`
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg group-hover:text-primary transition-colors max-[1149px]:text-base truncate">
                        {member.name}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground max-[1149px]:text-xs truncate">{member.role}</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      openRemoveConfirm(member)
                    }}
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 max-[1149px]:h-7 max-[1149px]:w-7 flex-shrink-0"
                    title={t('teamMembers.removeMember')}
                  >
                    <UserMinus className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 max-[1149px]:p-4 max-[1149px]:space-y-2">
                <div className="flex items-center space-x-2 text-sm max-[1149px]:text-xs">
                  <Mail className="h-4 w-4 text-muted-foreground max-[1149px]:h-3.5 max-[1149px]:w-3.5 flex-shrink-0" />
                  <span className="text-muted-foreground truncate">{member.email}</span>
                </div>
                
                {member.phone && (
                  <div className="flex items-center space-x-2 text-sm max-[1149px]:text-xs">
                    <Phone className="h-4 w-4 text-muted-foreground max-[1149px]:h-3.5 max-[1149px]:w-3.5 flex-shrink-0" />
                    <span className="text-muted-foreground truncate">{member.phone}</span>
                  </div>
                )}
                
                {member.location && (
                  <div className="flex items-center space-x-2 text-sm max-[1149px]:text-xs">
                    <MapPin className="h-4 w-4 text-muted-foreground max-[1149px]:h-3.5 max-[1149px]:w-3.5 flex-shrink-0" />
                    <span className="text-muted-foreground truncate">{member.location}</span>
                  </div>
                )}
                
                <div className="flex items-center justify-between max-[1149px]:flex-col max-[1149px]:items-stretch max-[1149px]:gap-2">
                  <div className="flex items-center space-x-1 text-xs text-muted-foreground max-[1149px]:text-[10px]">
                    <Calendar className="h-3 w-3 max-[1149px]:h-2.5 max-[1149px]:w-2.5" />
                    <span>{new Date(member.applied_date).toLocaleDateString()}</span>
                  </div>
                </div>

                {member.skills && member.skills.length > 0 && (
                  <div className="space-y-1 max-[1149px]:space-y-0.5">
                    <p className="text-xs font-medium text-muted-foreground max-[1149px]:text-[10px]">{t('teamMembers.skills')}</p>
                    <div className="flex flex-wrap gap-1 max-[1149px]:gap-0.5">
                      {member.skills.slice(0, 3).map((skill, index) => (
                        <Badge key={index} variant="outline" className="text-xs max-[1149px]:text-[10px] max-[1149px]:px-1.5 max-[1149px]:py-0.5">
                          {skill}
                        </Badge>
                      ))}
                      {member.skills.length > 3 && (
                        <Badge variant="outline" className="text-xs max-[1149px]:text-[10px] max-[1149px]:px-1.5 max-[1149px]:py-0.5">
                          +{member.skills.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Member Modal */}
      <AddMemberModal
        isOpen={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        onAddMembers={handleAddMembers}
        candidates={availableCandidates}
        loading={updating}
        canMassImport={canMassImport}
        onImportCandidates={canMassImport ? () => setShowImportModal(true) : undefined}
      />

      {team && canMassImport && (
        <AddCandidatesModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          canMassImport={canMassImport}
          onImportComplete={handleImportComplete}
          initialTeamId={team.id}
          lockTeamSelection
        />
      )}

      {/* Remove Confirmation Modal */}
      <Modal
        isOpen={showRemoveConfirm}
        onClose={() => {
          setShowRemoveConfirm(false)
          setMemberToRemove(null)
        }}
        title={t('teamMembers.removeMemberTitle')}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('teamMembers.removeMemberConfirm', { name: memberToRemove?.name })}
          </p>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowRemoveConfirm(false)
                setMemberToRemove(null)
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemoveMember}
              disabled={updating}
            >
              {updating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('common.removing')}
                </>
              ) : (
                <>
                  <UserMinus className="h-4 w-4 mr-2" />
                  {t('teamMembers.removeMemberCta')}
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

// Add Member Modal Component
interface AddMemberModalProps {
  isOpen: boolean
  onClose: () => void
  onAddMembers: (candidates: Candidate[]) => void
  candidates: Candidate[]
  loading: boolean
  canMassImport?: boolean
  onImportCandidates?: () => void
}

function AddMemberModal({
  isOpen,
  onClose,
  onAddMembers,
  candidates,
  loading,
  canMassImport,
  onImportCandidates
}: AddMemberModalProps) {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCandidates, setSelectedCandidates] = useState<Candidate[]>([])

  const filteredCandidates = candidates.filter(candidate =>
    (candidate.name && candidate.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (candidate.email && candidate.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (candidate.role && candidate.role.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  const handleToggleCandidate = (candidate: Candidate) => {
    setSelectedCandidates(prev => {
      const isSelected = prev.some(c => c.id === candidate.id)
      if (isSelected) {
        return prev.filter(c => c.id !== candidate.id)
      } else {
        return [...prev, candidate]
      }
    })
  }

  const handleSelectAll = () => {
    if (selectedCandidates.length === filteredCandidates.length) {
      setSelectedCandidates([])
    } else {
      setSelectedCandidates([...filteredCandidates])
    }
  }

  const handleAddMembers = () => {
    if (selectedCandidates.length > 0) {
      onAddMembers(selectedCandidates)
      setSelectedCandidates([])
      setSearchTerm('')
    }
  }

  const handleClose = () => {
    setSelectedCandidates([])
    setSearchTerm('')
    onClose()
  }

  const handleImportClick = () => {
    if (!canMassImport || !onImportCandidates) return
    handleClose()
    onImportCandidates()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('teamMembers.addMemberTitle')}>
      <div className="space-y-4 max-[1149px]:space-y-3">
        {canMassImport && onImportCandidates && (
          <div className="flex flex-col gap-3 p-3 border border-dashed rounded-lg bg-muted/40">
            <div className="text-sm text-muted-foreground">
              {t(
                'teamMembers.importCandidatesHelper',
                'Need to import new candidates before adding them?'
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              onClick={handleImportClick}
              disabled={loading}
            >
              <Upload className="h-4 w-4 mr-2" />
              {t('teamMembers.importCandidates')}
            </Button>
          </div>
        )}
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder={t('candidates.search', 'Search candidates...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={loading}
            className="w-full pl-10 pr-4 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background border-input text-foreground placeholder-muted-foreground disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>


        {/* Candidates List */}
        <div className="max-h-64 overflow-y-auto space-y-2">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-4 text-sm text-muted-foreground">
                {t('teamMembers.loadingCandidates', 'Loading candidates...')}
              </p>
            </div>
          ) : filteredCandidates.length === 0 ? (
            <div className="text-center py-8">
              <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">{t('offerDetail.noCandidatesAvailable', 'No candidates available')}</p>
              <p className="text-sm text-muted-foreground">
                {searchTerm 
                  ? t('offerDetail.tryDifferentSearchTerm', 'Try a different search term')
                  : t('teamMembers.noAvailableCandidates')}
              </p>
            </div>
          ) : (
            filteredCandidates.map((candidate) => {
              const isSelected = selectedCandidates.some(c => c.id === candidate.id)
              return (
                <div
                  key={candidate.id}
                  className={`p-3 border rounded-lg transition-colors ${
                    loading 
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer'
                  } ${
                    isSelected
                      ? 'border-primary bg-primary/5 dark:bg-primary/10'
                      : 'border-border hover:bg-muted'
                  }`}
                  onClick={() => {
                    if (loading) return
                    handleToggleCandidate(candidate)
                  }}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      isSelected
                        ? 'border-primary bg-primary'
                        : 'border-gray-300'
                    }`}>
                      {isSelected && (
                        <CheckCircle className="h-3 w-3 text-white" />
                      )}
                    </div>
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center text-white font-semibold text-xs">
                      {candidate.profile_picture ? (
                        <img
                          src={candidate.profile_picture}
                          alt={candidate.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        candidate.name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')
                          .toUpperCase()
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-sm text-foreground">{candidate.name}</h4>
                      <p className="text-xs text-muted-foreground">{candidate.email}</p>
                      <p className="text-xs text-muted-foreground">{candidate.role}</p>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>

        {selectedCandidates.length > 0 && (
          <div className="text-sm text-muted-foreground">
            {t('offerDetail.candidatesSelected', { count: selectedCandidates.length, defaultValue: `${selectedCandidates.length} candidate${selectedCandidates.length > 1 ? 's' : ''} selected` })}
          </div>
        )}

        <div className="flex justify-between items-center pt-4 border-t border-border">
          <Button
            variant="ghost"
            onClick={handleSelectAll}
            disabled={
              loading ||
              filteredCandidates.length === 0
            }
          >
            {selectedCandidates.length === filteredCandidates.length 
              ? t('common.deselectAll', 'Deselect All')
              : t('common.selectAll', 'Select All')
            }
          </Button>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleAddMembers}
              disabled={
                loading ||
                selectedCandidates.length === 0
              }
              loading={loading}
            >
              {loading 
                ? t('teamMembers.addingMembers', 'Adding Members...')
                : selectedCandidates.length > 0
                  ? (selectedCandidates.length === 1
                      ? t('teamMembers.addMemberCta', 'Add Member')
                      : t('teamMembers.addMembersCta', { count: selectedCandidates.length, defaultValue: `Add ${selectedCandidates.length} Members` }))
                  : t('teamMembers.addMembers', 'Add Members')
              }
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
